import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Search, Ticket, Clock, CheckCircle2, AlertCircle,
  User, Mail, Phone, MapPin, Package, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const TrackTicket = () => {
  const [searchParams] = useSearchParams();
  const ticketNumberFromUrl = searchParams.get('number');
  
  const [ticketNumber, setTicketNumber] = useState(ticketNumberFromUrl || '');
  const [ticket, setTicket] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ticketNumberFromUrl) {
      handleSearch();
    }
  }, [ticketNumberFromUrl]);

  const handleSearch = async () => {
    if (!ticketNumber.trim()) {
      toast.error('Please enter a ticket number');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/tickets/track/${ticketNumber.trim()}`
      );
      
      const data = await response.json();
      
      if (data.success) {
        setTicket(data.ticket);
        setResponses(data.responses);
      } else {
        toast.error('Ticket not found');
        setTicket(null);
        setResponses([]);
      }
    } catch (error) {
      console.error('Error tracking ticket:', error);
      toast.error('Failed to track ticket');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-700 border-blue-300',
      in_progress: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      resolved: 'bg-green-100 text-green-700 border-green-300',
      closed: 'bg-gray-100 text-gray-700 border-gray-300'
    };
    return colors[status] || colors.open;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-green-600',
      medium: 'text-yellow-600',
      high: 'text-orange-600',
      urgent: 'text-red-600'
    };
    return colors[priority] || colors.medium;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 mb-4">
            <Ticket className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-2">
            Track Your Ticket
          </h1>
          <p className="text-gray-600">Enter your ticket number to view status and updates</p>
        </div>

        {/* Search Box */}
        <Card className="mb-8 shadow-xl">
          <CardContent className="p-6">
            <div className="flex gap-3">
              <input
                type="text"
                value={ticketNumber}
                onChange={(e) => setTicketNumber(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Enter ticket number (e.g., NETIX-2025-0001)"
                className="flex-1 px-4 py-3 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              />
              <Button
                onClick={handleSearch}
                disabled={loading}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 px-8"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Search className="h-5 w-5 mr-2" />
                    Search
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Ticket Details */}
        {ticket && (
          <div className="space-y-6">
            {/* Status Card */}
            <Card className="shadow-xl">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl">{ticket.ticket_number}</CardTitle>
                    <p className="text-purple-100 mt-1">{ticket.service_type}</p>
                  </div>
                  <span className={`px-4 py-2 rounded-full border-2 font-semibold ${getStatusColor(ticket.status)}`}>
                    {ticket.status.toUpperCase().replace('_', ' ')}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-semibold">{ticket.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold">{ticket.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="text-sm text-gray-600">Mobile</p>
                      <p className="font-semibold">{ticket.mobile}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="text-sm text-gray-600">City</p>
                      <p className="font-semibold">{ticket.city}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="text-sm text-gray-600">Created</p>
                      <p className="font-semibold">{formatDate(ticket.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <AlertCircle className={`h-5 w-5 ${getPriorityColor(ticket.priority)}`} />
                    <div>
                      <p className="text-sm text-gray-600">Priority</p>
                      <p className={`font-semibold ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority.toUpperCase()}
                      </p>
                    </div>
                  </div>
                </div>

                {ticket.plan_interest && (
                  <div className="mt-4 p-4 bg-purple-50 rounded-lg flex items-start gap-3">
                    <Package className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">Plan Interest</p>
                      <p className="font-semibold text-purple-700">{ticket.plan_interest}</p>
                    </div>
                  </div>
                )}

                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-purple-600" />
                    Issue Description
                  </h4>
                  <p className="text-gray-700">{ticket.issue_description}</p>
                </div>
              </CardContent>
            </Card>

            {/* Response Timeline */}
            {responses.length > 0 && (
              <Card className="shadow-xl">
                <CardHeader className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
                  <CardTitle>Updates & Responses</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {responses.map((response, index) => (
                      <div
                        key={response.response_id}
                        className="flex gap-4 pb-4 border-b last:border-0"
                      >
                        <div className="flex-shrink-0">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                            response.sender_type === 'support'
                              ? 'bg-gradient-to-r from-purple-500 to-blue-500'
                              : 'bg-gradient-to-r from-green-400 to-blue-500'
                          }`}>
                            <User className="h-5 w-5 text-white" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-gray-900">
                              {response.sender_name}
                            </span>
                            <span className="text-sm text-gray-500">
                              {formatDate(response.created_at)}
                            </span>
                          </div>
                          <p className="text-gray-700">{response.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Support Info */}
            <Card className="shadow-xl bg-gradient-to-r from-yellow-50 to-orange-50">
              <CardContent className="p-6">
                <div className="text-center">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Need immediate help?
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Our support team is available during working hours
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a
                      href="tel:+919319919988"
                      data-testid="track-ticket-support-mobile-link"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
                    >
                      <Phone className="h-5 w-5" />
                      +91 93199 19988
                    </a>
                    <a
                      href="tel:06276459015"
                      data-testid="track-ticket-office-phone-link"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-blue-600 text-blue-700 rounded-lg font-semibold hover:bg-blue-50 transition-all"
                    >
                      <Phone className="h-5 w-5" />
                      Office: 06276 - 459015
                    </a>
                    <a
                      href="mailto:care@netixfibernet.com"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-purple-600 text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-all"
                    >
                      <Mail className="h-5 w-5" />
                      care@netixfibernet.com
                    </a>
                  </div>
                  <p className="text-sm text-gray-600 mt-4">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Working Hours: 10:00 AM - 6:00 PM
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* No Ticket Found */}
        {!loading && !ticket && ticketNumber && (
          <Card className="shadow-xl">
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Ticket Not Found
              </h3>
              <p className="text-gray-600">
                Please check your ticket number and try again
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TrackTicket;
